import { useState, type FormEvent } from "react";
import { updatePassword } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase.ts";
import { useSession } from "../lib/session.tsx";

export const MIN_PASSWORD_LENGTH = 12;

/**
 * Shown instead of the app while the account still carries the temporary
 * password it was provisioned with (users/{uid}.mustChangePassword). The flag
 * is cleared only after Firebase Auth has accepted the new password.
 */
export function PasswordChange() {
  const { t, profile, logout } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("pw.tooShort", { n: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t("pw.mismatch"));
      return;
    }
    const user = auth.currentUser;
    if (!user || !profile) return;
    setBusy(true);
    try {
      await updatePassword(user, password);
      await updateDoc(doc(db, "users", profile.uid), { mustChangePassword: false });
    } catch (err) {
      const code = err instanceof Error && "code" in err ? String(err.code) : "";
      setError(code === "auth/requires-recent-login" ? t("pw.relogin") : t("pw.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">{t("app.name")}</div>
        <form onSubmit={submit}>
          <h1 style={{ fontSize: "1.05rem", marginBottom: 4 }}>{t("pw.title")}</h1>
          <p className="faint small" style={{ marginBottom: 18 }}>
            {t("pw.lede", { name: profile?.displayName ?? "" })}
          </p>
          {error && <div className="callout callout-danger">{error}</div>}
          <div className="field">
            <label htmlFor="new-password">{t("pw.new")}</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="confirm-password">{t("pw.confirm")}</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="primary"
            disabled={busy}
            style={{ width: "100%", marginTop: 6 }}
          >
            {busy ? t("auth.working") : t("pw.submit")}
          </button>
          <button
            type="button"
            className="subtle"
            onClick={() => void logout()}
            style={{ width: "100%", marginTop: 8 }}
          >
            {t("nav.signOut")}
          </button>
        </form>
      </div>
    </div>
  );
}
